import httpClient from '@/utilities/service/httpClient'
import AbstractApi from '@/api/abstracts/AbstractApi'
import SitesApi from '@/api/services/sales/bid/spec/sites/SitesApi'
import ArmsApi from '@/api/services/sales/bid/spec/arms/ArmsApi'
import AnalysisApi from '@/api/services/sales/bid/spec/analysis/AnalysisApi'
import PaperworkApi from '@/api/services/sales/bid/spec/paperwork/PaperworkApi'
import DocumentTypeEnum from '@/api/enums/DocumentTypeEnum'
// eslint-disable-next-line prettier/prettier
import type { AxiosResponse } from 'axios'
import type { BidNumber, SiteId } from '@/api/types/general/GeneralType'
import type { SpecificationsType } from '@/api/types/response/SpecificationsType'
import type { BidExportDocumentType, BidExportKMLType } from '@/api/types/response/BidExportDocumentType'
import type { AskReferencePayloadType } from '@/api/types/request/AskReferencePayloadType'
import type { AskReferenceType } from '@/api/types/response/AskReferenceType'
import type { ExportSitesType, ImportSitesType } from '@/api/types/response/SiteType'
import type { SiteType } from '@/api/types/general/SpecificationType'

type ExportDocumentType = {
  [DocumentTypeEnum.kml]: BidExportKMLType
  [DocumentTypeEnum.csv]: BidExportDocumentType
  [DocumentTypeEnum.xml]: AxiosResponse
}

/**
 * @property string apiEnd /api/sales/bid/spec
 */
export default class SpecificationsApi extends AbstractApi {
  detail(bidNumber: BidNumber): Promise<SpecificationsType> {
    return httpClient.get(`${this.apiEnd}/${bidNumber}`)
  }

  askReference(bidNumber: BidNumber, payload: AskReferencePayloadType): Promise<AskReferenceType> {
    return httpClient.post(`${this.apiEnd}/${bidNumber}/siteref`, payload)
  }

  exportDocument<K extends DocumentTypeEnum>(id: string, serviceName: string, type: K): Promise<ExportDocumentType[K]> {
    return httpClient.post(`${this.apiEnd}/export/${type}/${id}/summary/${serviceName}`)
  }

  sites(bidNumber: BidNumber): SitesApi {
    return new SitesApi(`${this.apiEnd}/${bidNumber}`)
  }

  arms(bidNumber: BidNumber, id: number): ArmsApi {
    return new ArmsApi(`${this.apiEnd}/${bidNumber}/${id}/arms`)
  }

  analysis(bidNumber: BidNumber): AnalysisApi {
    return new AnalysisApi(`${this.apiEnd}/${bidNumber}`)
  }

  paperwork(bidNumber: BidNumber, site: SiteId): PaperworkApi {
    return new PaperworkApi(`${this.apiEnd}/${bidNumber}/${site}/paperwork`)
  }

  export(bidNumber: BidNumber): Promise<ExportSitesType> {
    return httpClient.get(`${this.apiEnd}/export/json/${bidNumber}`)
  }

  import(bidNumber: BidNumber, date: string, sites: SiteType[]): Promise<ImportSitesType> {
    return httpClient.post(`${this.apiEnd}/import/json/${bidNumber}`, {
      startDate: date,
      data: sites,
    })
  }
}
